const LOCATION_STORAGE_KEY = '_LS_LOCATION_NEW';
const LOCATION_API_URL = 'https://ipinfo.io/json?token=c64f2fbdde5898';
let locationPromise = null;

function getUserLocation () {
  const cached = localStorage.getItem(LOCATION_STORAGE_KEY);

  if (cached) {
    return Promise.resolve(JSON.parse(cached));
  }

  if (!locationPromise) {
    locationPromise = fetch(LOCATION_API_URL)
      .then(r => r.json())
      .then(r => {
        const [latitude, longitude] = (r.loc || '').split(',').map(Number);

        if (!latitude || !longitude) return { error: true };

        const location = { ...r, latitude, longitude };

        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
        return location;
      })
      .catch(() => ({ error: true }))
      .finally(() => {
        locationPromise = null;
      });
  }

  return locationPromise;
}

class LocationSearch extends HTMLElement{
  constructor() {
    super();

    this.applicationId = "HKE91WQ52Z";
    this.algoliaKey = "2f63f57f15703ca0f2bd0557e051f16b";
    this.indexName = 'jigshop_collections';
    this.mapboxApiUrl = 'https://api.mapbox.com/search/geocode/v6/forward';
    this.mapboxToken = 'pk.eyJ1IjoiZGFsYmllIiwiYSI6ImNtdXZkMXA0ejFyenozMHNjaDkwbTN3Z3kifQ.aj6PX-e9xqZ3BilcXwFOfw';
    this.geocodeTimeout = 3000;
  }

  connectedCallback () {
    this.algoliaLoaded = false;
    this.lastSearchedTerm = '';
    this.searchForm = this.querySelector('form');
    this.searchBar = this.querySelector("input[type='search']");
    this.searchResults = this.querySelector('.search-results');

    // this.searchBar.addEventListener('change', this.onInputChange.bind(this));
    this.searchForm.addEventListener('submit', this.onInputChange.bind(this));

    this.searchBar.addEventListener('keyup', debounce(this.onInputChange.bind(this), 500));

    this.loadAlgolia = this.loadAlgolia.bind(this);
  }

  async initAlgolia () {
    if (!this.algoliaLoaded) {
      await this.loadAlgolia();
    }

    const { algoliasearch } = window["algoliasearch"];
    this.algoliasearch = this.algoliasearch || algoliasearch;
    this.searchClient = this.searchClient || this.algoliasearch(this.applicationId, this.algoliaKey);

    this.estimateLocation()
      .then(async res => {
        const coords = { lat: res.latitude, lng: res.longitude};
        const address = `${res.city} ${res.country_name}`;
        const response = await this.searchIndex('', coords);


        this.renderAddress(address);
        if (response) this.renderResults(response, coords);
      });
  }

  async loadAlgolia () {
    if (this.algoliaLoaded) return;

    const scripts = [
      'https://cdn.jsdelivr.net/npm/algoliasearch@5.42.0/dist/algoliasearch.umd.js',
      'https://cdn.jsdelivr.net/npm/instantsearch.js@4.79.1/dist/instantsearch.production.min.js'
    ];

    const promises = [];

    for (let src of scripts) {
      promises.push(new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      }));
    }

    await Promise.all(promises);

    this.algoliaLoaded = true;
  }

  async onInputChange(e) {
    e.preventDefault();

    const term = this.searchBar.value.trim();

    if (term.length < 3 || term === this.lastSearchedTerm) return;

    this.lastSearchedTerm = term;
    this.classList.add('loading');

    const locationResponse = await this.searchForLocation(term);
    const response = await this.searchIndex(term, locationResponse?.coords);

    this.renderAddress(locationResponse?.address);
    if (response) this.renderResults(response, locationResponse?.coords);

    this.classList.remove('loading');
  }

  async searchForLocation (term) {
    const cacheKey = `_geo_us_${term.toLowerCase()}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      const parsed = JSON.parse(cached);
      if (!isDateGreater(new Date(), parsed.date, 30)) {
        return parsed.result;
      }
    }

    const params = new URLSearchParams({
      q: term,
      types: 'place',
      country: 'us',
      limit: 1,
      access_token: this.mapboxToken
    });
    let data;

    // Time out so a slow geocoder falls back to a plain text search instead of hanging
    try {
      const response = await fetch(`${this.mapboxApiUrl}?${params}`, {
        signal: AbortSignal.timeout(this.geocodeTimeout)
      });
      if (!response.ok) return null;
      data = await response.json();
    } catch (e) {
      return null;
    }

    const feature = data.features?.[0];

    if (!feature) return null;

    const [lon, lat] = feature.geometry.coordinates;
    const { name, context } = feature.properties;
    const address = [name, context?.region?.name, context?.country?.name].filter(Boolean).join(', ');

    const result = {
      coords: { lat, lng: lon },
      address
    };

    localStorage.setItem(cacheKey, JSON.stringify({ date: new Date(), result }));

    return result;
  }

  async searchIndex (term, coords) {
    const storageKey = `si-${term}-${encodeURI(JSON.stringify(coords))}`;
    const searchStorage = localStorage.getItem(storageKey);

    if (searchStorage) {
      const json = JSON.parse(searchStorage);
      if (!isDateGreater(new Date(), json.date, 5)) {
        return json.response;
      }
    }

    const params = {};

    if (coords) {
      params.aroundLatLng = `${coords.lat}, ${coords.lng}`;
    } else {
      params.query = term;
    }

    const response = await this.searchClient.searchSingleIndex({
      indexName: this.indexName,
      searchParams: {
        ...params,
        "filters": "NOT meta.custom.exclude_from_locations_list:true"
      }
    });

    localStorage.setItem(storageKey, JSON.stringify({
      date: new Date(),
      response: response
    }));

    return response;
  }

  renderAddress (address) {
    if (address) {
      this.searchResults.querySelector('#predictive-search-results-term').innerHTML = `<span>Showing results for: ${address}</span>`;
    } else {
      this.searchResults.querySelector('#predictive-search-results-term').innerHTML = null;
    }
  }

  renderResults (response, coords) {
    const resultContainer = this.searchResults.querySelector('#predictive-search-results-queries-list');

    resultContainer.innerHTML = '';
    resultContainer.innerHTML += response.hits.map((res, key) => {
      const distance = coords ? `<span>
        ${this.calculateDistance(coords, res._geoloc).toFixed(2)} miles
      </span>` : null;

      return `
      <li id='predictive-search-option-collection-${key}' class='predictive-search__list-item' role='option' aria-selected='false'>
        <a href='/collections/${res.handle}' class='predictive-search__item' tabindex='-1'>
          <div class='predictive-search__item-content predictive-search__item-content--centered'>
            <p class='predictive-search__item-heading h5'>
              ${res.title} ${distance ? distance : ''}
            </p>
          </div>
        </a>
      </li>
      `;
    }).join(' ');

    this.searchResults.style.display = 'block';

    this.setResultsMaxHeight();
  }

  calculateDistance(coords1, coords2) {
    const { lat: lat1, lng: lon1 } = coords1;
    const { lat: lat2, lng: lon2 } = coords2;
    const degToRad = x => x * Math.PI / 180;
    const R = 6371;
    const halfDLat = degToRad(lat2 - lat1) / 2;
    const halfDLon = degToRad(lon2 - lon1) / 2;
    const a = Math.sin(halfDLat) * Math.sin(halfDLat) +
      Math.cos(degToRad(lat1)) * Math.cos(degToRad(lat2)) *
      Math.sin(halfDLon) * Math.sin(halfDLon);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c) * 0.621371;
  }

  async estimateLocation () {
    return getUserLocation();
  }

  setResultsMaxHeight() {
    const headerHeight = this.querySelector('.search-modal__header').offsetHeight;
    const formHeight = this.querySelector('form').offsetHeight;

    this.searchResults.style.maxHeight = `calc(${this.parentElement.offsetHeight - (headerHeight + formHeight) + 'px'} - 3rem)`;
  }
}

class NearestCities extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback () {
    this.graphQlKey = '0a6a16190f2be357221f6ad5cc1abb69';

    this.init();
  }

  async init () {
    const [ locations, { error, latitude, longitude } ] = await Promise.all([
      this.searchLocations(),
      this.fetchLocation()
    ]);

    if (error) {
      this.outerHTML = null;
      return;
    }

    let closestLocations = this.getClosestLocations(
      locations.data.collections.nodes.filter(location => location.include && location.include.value === 'false' || !location.include),
      latitude,
      longitude
    );

    closestLocations = closestLocations.map(location => {
      return {
        title: location.title,
        dist: location.dist,
        url: location.onlineStoreUrl
      }
    });

    closestLocations.length = 3;

    this.innerHTML = `
      <div class="page-width h4">
        <div>Top cities near you:</div>
      </div>
      
      <div class='nearest-scroll'>
        ${closestLocations.map(location => {
          return `<a href="${location.url}" class="button button--secondary">
            <svg width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M6.14594 16C3.79479 13.4172 -0.204146 9.75073 0.00812088 5.76245C0.355399 2.52003 2.97092 0 6.14594 0C9.32095 0 11.9365 2.52003 12.2838 5.76245C12.4301 10.1172 8.81724 13.1305 6.14594 16Z" fill="#111154"/>
              <path d="M8.59329 6.06912C8.59329 7.52495 7.47143 8.70514 6.08754 8.70514C4.70365 8.70514 3.58179 7.52495 3.58179 6.06912C3.58179 4.61329 4.70365 3.43311 6.08754 3.43311C7.47143 3.43311 8.59329 4.61329 8.59329 6.06912Z" fill="white"/>
            </svg>
    
            <span>${location.title}</span>
          </a>`
        }).join('')}
      </div>
    `;
  }

  async fetchLocation () {
    return getUserLocation();
  }

  async searchLocations () {
    return await fetch('/api/2025-10/graphql.json', {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": this.graphQlKey
      },
      body: JSON.stringify({
        query: `query LocationCollections {
        collections(first: 250) {
          nodes {
            title
            onlineStoreUrl
            lon: metafield(namespace: "custom", key: "lon") {
              id,
              value
            },
            lat: metafield(namespace: "custom", key: "lat") {
              id,
              value
            }
            include: metafield(namespace: "custom", key: "exclude_from_locations_list") {
              id,
              value
            }
          }
        }
      }`
      })
    }).then(r => r.json());
  }

  getClosestLocations (locations, lat, lon) {
    const collections = [];

    for (let collection of locations) {
      if (!collection.lat || !collection.lon)
        continue;

      const dist = this.calculateDistance(
        { lat: collection.lat.value, lon: collection.lon.value },
        { lat, lon }
      );

      collection.dist = dist;
      collections.push(collection);
    }

    const cols = collections.sort((a,b) => {
      if (a.dist > b.dist) return 1;
      if (a.dist < b.dist) return -1;
      return 0;
    });

    return cols;
  }

  calculateDistance(coords1, coords2) {
    const { lat: lat1, lon: lon1 } = coords1;
    const { lat: lat2, lon: lon2 } = coords2;
    const degToRad = x => x * Math.PI / 180;
    const R = 6371;
    const halfDLat = degToRad(lat2 - lat1) / 2;
    const halfDLon = degToRad(lon2 - lon1) / 2;
    const a = Math.sin(halfDLat) * Math.sin(halfDLat) +
      Math.cos(degToRad(lat1)) * Math.cos(degToRad(lat2)) *
      Math.sin(halfDLon) * Math.sin(halfDLon);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c) * 0.621371;
  }
}

customElements.define('nearest-cities', NearestCities);
customElements.define('location-search', LocationSearch);

/**
 * Check if d2 is greater than d1
 * @param {String|Object} d1 Datestring or Date object
 * @param {String|Object} d2 Datestring or Date object
 * @param {Number} days Optional number of days to add to d1
 */
function isDateGreater (d1, d2, days) {
  d1 = new Date(d1);
  return +new Date(d2) > d1.setDate(d1.getDate() + (days||0))
}